import { useCallback, useEffect, useRef, useState } from 'react';
import type { ApiClient } from '../api/client';
import { ApiError, QuotaError } from '../api/client';
import type { GameId, GameSpec, ScanResponse, TelemetryBody } from '../api/types';
import { useSubscriptionStore } from '../stores/subscriptionStore';
import { track } from '../services/analyticsService';
import { captureScanImage } from '../utils/imagePrep';

export type ScanPhase =
  | 'idle'
  | 'starting'
  | 'aim'
  | 'scanning'
  | 'confirm'
  | 'reveal'
  | 'manual'
  | 'error';

export interface DraftPlay {
  main: number[];
  bonus: number[];
}

export interface TicketDraft {
  /** Game identified from the physical ticket image, or the active game for manual entry. */
  gameId?: GameId;
  /** One entry per play on the ticket. Most tickets have exactly one. */
  plays: DraftPlay[];
  drawDate?: string;
  cost?: number;
  /** How the draft got here when it reached `confirm`. */
  source: 'llm' | 'manual';
  /** Confidence: 1 = full LLM read, 0.5 = any play partial, 0 = manual. */
  confidence: number;
}

export interface ScanError {
  /** ApiError code, or a local error tag (`camera`, `scan_failed`). */
  code: string;
  message: string;
}

export interface UseScannerValue {
  phase: ScanPhase;
  draft: TicketDraft | null;
  lastError: ScanError | null;
  /** True while the Worker OCR call is in flight. */
  scanning: boolean;
  startCamera: () => Promise<HTMLVideoElement | null>;
  capture: () => Promise<void>;
  setDraft: (next: TicketDraft | null) => void;
  goManual: () => void;
  goReveal: (finalDraft: TicketDraft) => void;
  reset: () => void;
  dismissError: () => void;
  videoRef: React.RefObject<HTMLVideoElement>;
}

export interface UseScannerOptions {
  api: ApiClient;
  game: GameSpec & { id: GameId };
  /** Fire-and-forget telemetry sink. Defaults to a no-op. */
  sendTelemetry?: (body: TelemetryBody) => void;
  /**
   * Invoked when the free monthly scan cap is reached. Fires both on the
   * pre-flight check (store-cached quota) and on server-side 402. The scanner
   * resets itself to `idle` either way; the page is expected to open the
   * paywall in response.
   */
  onQuotaExceeded?: () => void;
}

/**
 * Pure quota-check predicate extracted so it can be tested without driving
 * the full hook lifecycle (camera, video element, canvas).
 * Returns true when the caller should short-circuit before uploading because
 * the store's cached quota says the user is already capped for this month.
 */
export function isQuotaExhausted(quota: {
  scansCapThisMonth: number | 'unlimited' | null;
  scansUsedThisMonth: number | null;
}): boolean {
  return (
    quota.scansCapThisMonth !== 'unlimited' &&
    quota.scansCapThisMonth !== null &&
    quota.scansUsedThisMonth !== null &&
    quota.scansUsedThisMonth >= quota.scansCapThisMonth
  );
}

/**
 * Scan state machine. Flow is: idle → aim → scanning → confirm → reveal.
 *
 * OCR runs exclusively on the backend via `POST /v1/ocr/scan` — the browser's job is just to
 * capture a frame, downscale it, send it up, and render the result. Worker-side OpenAI vision
 * handles both the digit recognition and the "reason: unreadable" signal. No Tesseract.
 */
export function useScanner(options: UseScannerOptions): UseScannerValue {
  const { api, game, sendTelemetry, onQuotaExceeded } = options;

  const [phase, setPhase] = useState<ScanPhase>('idle');
  const [draft, setDraft] = useState<TicketDraft | null>(null);
  const [lastError, setLastError] = useState<ScanError | null>(null);
  const [scanning, setScanning] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const stopStream = useCallback(() => {
    const s = streamRef.current;
    if (s) {
      for (const t of s.getTracks()) {
        try {
          t.stop();
        } catch {
          /* already stopped */
        }
      }
    }
    streamRef.current = null;
    if (videoRef.current) {
      try {
        videoRef.current.srcObject = null;
      } catch {
        /* ignore */
      }
    }
  }, []);

  const abortInFlight = useCallback(() => {
    const a = abortRef.current;
    abortRef.current = null;
    if (a) a.abort();
  }, []);

  useEffect(() => {
    return () => {
      stopStream();
      abortInFlight();
    };
  }, [stopStream, abortInFlight]);

  const startCamera = useCallback<UseScannerValue['startCamera']>(async () => {
    setPhase('starting');
    setLastError(null);
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('Camera is not available in this browser');
      }
      if (!window.isSecureContext) {
        throw new Error('Camera requires HTTPS. Use localhost, ngrok, or deploy to HTTPS.');
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });
      streamRef.current = stream;
      const v = videoRef.current;
      if (v) {
        v.srcObject = stream;
        v.setAttribute('playsinline', 'true');
        v.muted = true;
        try {
          await v.play();
        } catch {
          /* play() throws if the gesture is stale; srcObject still renders. */
        }
      }
      setPhase('aim');
      return v;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setLastError({ code: 'camera', message });
      setPhase('manual');
      return null;
    }
  }, []);

  const capture = useCallback<UseScannerValue['capture']>(async () => {
    if (phase !== 'aim') return;
    const video = videoRef.current;
    if (!video) return;

    // Pre-flight: if the store already knows the user is out of free scans,
    // skip the image encode + upload entirely and pop the paywall.
    if (isQuotaExhausted(useSubscriptionStore.getState())) {
      stopStream();
      setPhase('idle');
      onQuotaExceeded?.();
      return;
    }

    setPhase('scanning');
    setScanning(true);
    setLastError(null);
    const scanStartedAt = Date.now();
    track('scan_start', { gameId: game.id, source: 'camera' });

    let imagePayload: ReturnType<typeof captureScanImage>;
    try {
      imagePayload = captureScanImage(video);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not capture frame';
      track('scan_failure', {
        gameId: game.id,
        source: 'camera',
        outcome: 'capture_failed',
        durationMs: Date.now() - scanStartedAt,
        errorCode: 'scan_failed',
      });
      setLastError({ code: 'scan_failed', message });
      setPhase('manual');
      setScanning(false);
      return;
    }

    const abort = new AbortController();
    abortRef.current = abort;
    try {
      const res = await api.post<ScanResponse>(
        '/v1/ocr/scan',
        { imageBase64: imagePayload.imageBase64, gameId: game.id },
        { signal: abort.signal }
      );
      const next: TicketDraft = {
        gameId: res.ticket.gameId ?? game.id,
        plays: res.ticket.plays.map((p) => ({ main: p.main, bonus: p.bonus })),
        drawDate: res.ticket.drawDate,
        cost: res.ticket.cost,
        source: 'llm',
        confidence: res.ticket.partial ? 0.5 : 1,
      };
      track('scan_success', {
        gameId: next.gameId ?? game.id,
        source: 'llm',
        outcome: res.ticket.partial ? 'partial' : 'complete',
        durationMs: Date.now() - scanStartedAt,
      });
      setDraft(next);
      if (res.ticket.partial) {
        setLastError({
          code: 'partial',
          message: "Couldn\u2019t read every number — please fill in the blanks.",
        });
      }
      setPhase('confirm');
      sendTelemetry?.({
        tesseractScore: 0,
        tesseractDigits: 0,
        llmParsed: {
          main: next.plays[0]?.main ?? [],
          bonus: next.plays[0]?.bonus ?? [],
        },
        usedLlm: true,
        outcome: res.ticket.partial ? 'llm_partial' : 'llm_complete',
      });
    } catch (err) {
      if (err instanceof QuotaError) {
        track('scan_failure', {
          gameId: game.id,
          source: 'camera',
          outcome: 'quota',
          durationMs: Date.now() - scanStartedAt,
          errorCode: 'scan_cap',
        });
        // Server-side pre-flight missed (cold start, stale client cache, race).
        // Stop the camera, drop back to idle, and let the page open the paywall.
        stopStream();
        setPhase('idle');
        setScanning(false);
        onQuotaExceeded?.();
        return;
      }
      const apiErr =
        err instanceof ApiError
          ? err
          : new ApiError(0, 'unknown', err instanceof Error ? err.message : 'Scan failed');
      if (apiErr.code === 'aborted') return;
      track('scan_failure', {
        gameId: game.id,
        source: 'camera',
        outcome: 'error',
        durationMs: Date.now() - scanStartedAt,
        errorCode: apiErr.code,
      });

      sendTelemetry?.({
        tesseractScore: 0,
        tesseractDigits: 0,
        usedLlm: true,
        outcome: `worker_error:${apiErr.code}`,
      });

      if (apiErr.code === 'unreadable') {
        setLastError({
          code: apiErr.code,
          message: "Couldn\u2019t read that ticket — please enter the numbers manually.",
        });
        setPhase('manual');
        return;
      }
      if (apiErr.code === 'bad_image' || apiErr.code === 'image_too_large') {
        setLastError({ code: apiErr.code, message: apiErr.message });
        setPhase('manual');
        return;
      }
      if (apiErr.code === 'rate_limited' || apiErr.code === 'kill_switch') {
        setLastError({
          code: apiErr.code,
          message:
            apiErr.code === 'rate_limited'
              ? apiErr.message
              : 'Scanning is temporarily off — you can still enter numbers manually.',
        });
        setPhase('manual');
        return;
      }
      setLastError({
        code: apiErr.code,
        message: 'Offline or upstream issue — please enter the numbers manually.',
      });
      setPhase('manual');
    } finally {
      setScanning(false);
      if (abortRef.current === abort) abortRef.current = null;
    }
  }, [phase, game, api, sendTelemetry, onQuotaExceeded, stopStream]);

  const goManual = useCallback(() => {
    track('manual_ticket_entry', { gameId: game.id, source: phase === 'idle' ? 'manual' : 'scan_fallback' });
    setDraft({ gameId: game.id, plays: [{ main: [], bonus: [] }], source: 'manual', confidence: 0 });
    setLastError(null);
    setPhase('manual');
  }, [game.id, phase]);

  const goReveal = useCallback(
    (finalDraft: TicketDraft) => {
      stopStream();
      abortInFlight();
      setDraft(finalDraft);
      setLastError(null);
      setPhase('reveal');
    },
    [stopStream, abortInFlight]
  );

  const reset = useCallback(() => {
    stopStream();
    abortInFlight();
    setDraft(null);
    setLastError(null);
    setScanning(false);
    setPhase('idle');
  }, [stopStream, abortInFlight]);

  const dismissError = useCallback(() => setLastError(null), []);

  return {
    phase,
    draft,
    lastError,
    scanning,
    startCamera,
    capture,
    setDraft,
    goManual,
    goReveal,
    reset,
    dismissError,
    videoRef,
  };
}
